import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ClipboardList,
  HandHelping,
  Sparkles,
  BookOpen,
  Link2,
  CalendarCheck,
  Vote,
} from 'lucide-react';

const GetInvolvedSection = ({ isDarkMode }) => {
  const [legislatorsExpanded, setLegislatorsExpanded] = useState(false);

  useEffect(() => {
    if (window.location.hash === '#for-legislators') {
      setLegislatorsExpanded(true);
    }
  }, []);

  useEffect(() => {
    if (!legislatorsExpanded || window.location.hash !== '#for-legislators') return;
    const el = document.getElementById('for-legislators');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [legislatorsExpanded]);

  const benefits = [
    {
      icon: BookOpen,
      title: 'Easily onboard them to new legislation',
      description: 'Share bills and updates so constituents can follow what matters to them.',
    },
    {
      icon: Link2,
      title: 'A single place for all your links',
      description: 'Official social media, campaign sites, and official pages in one profile.',
    },
    {
      icon: CalendarCheck,
      title: 'Keep them up to date on your activities',
      description: 'Constituents see your activity and stay informed without hunting across the web.',
    },
    {
      icon: Vote,
      title: 'Get them engaged in government',
      description: 'One hub where people can find you, follow your work, and participate in civic life.',
    },
  ];

  return (
    <section className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} py-16 md:py-20`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className={`text-4xl md:text-5xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-6`}>
          Get Involved
        </h2>
        <p className={`text-xl md:text-2xl ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-10`}>
          We're learning as we grow. If you have feedback, ideas, or want to get involved, we'd love to hear from you.
        </p>
        <a
          href="mailto:founders@civicduty.app"
          className="inline-flex items-center px-8 py-4 bg-[#ef4444] text-white text-lg font-semibold rounded-lg hover:bg-[#dc2626] transition-colors shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Contact Us
        </a>

        {/* For Legislators - collapsible section */}
        <div
          id="for-legislators"
          className={`mt-12 text-left ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} rounded-2xl border overflow-hidden scroll-mt-24 shadow-lg`}
        >
          <button
            type="button"
            onClick={() => setLegislatorsExpanded((v) => !v)}
            className={`w-full flex items-center justify-between px-6 py-5 text-left ${isDarkMode ? 'text-gray-100 hover:bg-gray-700/50' : 'text-gray-900 hover:bg-gray-100/80'} transition-colors`}
          >
            <span className="font-semibold text-xl">For legislators</span>
            <ChevronDown
              className={`w-6 h-6 flex-shrink-0 transition-transform duration-200 ${legislatorsExpanded ? 'rotate-180' : ''}`}
            />
          </button>
          {legislatorsExpanded && (
            <div
              className={`px-6 pb-8 pt-6 ${isDarkMode ? 'border-t border-gray-700' : 'border-t border-gray-200'}`}
            >
              {/* Intro */}
              <p
                className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-8 leading-relaxed`}
              >
                CivicDuty helps you reach constituents in one place. Fill out a short form to claim your
                profile—we'll add your official and campaign links and your about page so voters can find
                everything about you without searching across the web.
              </p>

              {/* Three main blocks */}
              <div className="space-y-6">
                {/* Why the form + How you can assist - side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div
                    className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-700/50' : 'bg-white shadow-sm border border-gray-100'}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-red-900/40' : 'bg-red-50'}`}
                      >
                        <ClipboardList className="w-4 h-4 text-[#ef4444]" />
                      </div>
                      <h3 className={`font-semibold text-base ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        Why fill out the form?
                      </h3>
                    </div>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm leading-snug`}>
                      The form lets us verify you and gather your official social media and campaign links. We
                      consolidate everything into one profile and fill out your about page so constituents have a
                      single, trusted place to find and follow you.
                    </p>
                  </div>
                  <div
                    className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-700/50' : 'bg-white shadow-sm border border-gray-100'}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-red-900/40' : 'bg-red-50'}`}
                      >
                        <HandHelping className="w-4 h-4 text-[#ef4444]" />
                      </div>
                      <h3 className={`font-semibold text-base ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        How you can assist
                      </h3>
                    </div>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm leading-snug`}>
                      Email us from your official .ga.gov address to request your profile. Once we confirm, you'll
                      get a short informational form. Your responses help us keep your links and about section
                      accurate so constituents always see up-to-date information.
                    </p>
                  </div>
                </div>

                {/* Benefits of CivicDuty */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-900/40' : 'bg-red-50'}`}
                    >
                      <Sparkles className="w-5 h-5 text-[#ef4444]" />
                    </div>
                    <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      Benefits of CivicDuty for reaching constituents
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {benefits.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={index}
                          className={`rounded-xl p-4 flex gap-3 text-left transition-all duration-200 hover:shadow-md ${isDarkMode ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-white border border-gray-100 hover:border-gray-200 hover:shadow'}`}
                        >
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}
                          >
                            <Icon className="w-4 h-4 text-[#ef4444]" />
                          </div>
                          <div>
                            <h4 className={`font-medium text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-0.5`}>
                              {item.title}
                            </h4>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>
                              {item.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div
                className={`mt-8 pt-6 text-center ${isDarkMode ? 'border-t border-gray-700' : 'border-t border-gray-200'}`}
              >
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-base md:text-lg mb-5`}>
                  Ready to get started? Email us from your official .ga.gov address to request your profile.
                </p>
                <a
                  href="mailto:founders@civicduty.app?subject=Request Access for Political Profile"
                  className="inline-flex items-center px-8 py-4 text-lg font-semibold bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors shadow-md hover:shadow-lg"
                >
                  Request Access
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GetInvolvedSection;
